import { motion } from 'motion/react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import { formatCurrency, formatDateTime } from '../../helpers/formatters'
import { toOrderStatusBadgeClasses, toOrderStatusLabel } from '../../helpers/status'
import { customerMotionTransition } from '../customer-area'
import { HelpCircleIcon, joinClassNames, formatCount } from './dashboardShared'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type DashboardRecentOrderCardProps = {
  order: CustomerOrder
  selected: boolean
  reducedMotion: boolean
  language: ShopLanguage
  locale: ShopLocale
  isItalian: boolean
  t: TranslateFn
  onSelectOrder: (orderId: string) => void
  onOpenOrderDetail: (orderId: string) => void
  onOpenCatalog: () => void
  onRequestSupport: (order: CustomerOrder) => void
}

export const DashboardRecentOrderCard = ({
  order,
  selected,
  reducedMotion,
  language,
  locale,
  isItalian,
  t,
  onSelectOrder,
  onOpenOrderDetail,
  onOpenCatalog,
  onRequestSupport,
}: DashboardRecentOrderCardProps) => {
  const createdAtLabel = formatDateTime(order.createdAt, locale, t('common.never'))
  const firstProductName = order.items[0]?.productName?.trim() || (isItalian ? 'Articolo ordine' : 'Order item')
  const additionalItems = Math.max(order.itemCount - 1, 0)
  const additionalItemsLabel = isItalian
    ? `+${formatCount(additionalItems, locale)} ${additionalItems === 1 ? 'prodotto' : 'prodotti'}`
    : `+${formatCount(additionalItems, locale)} ${additionalItems === 1 ? 'product' : 'products'}`
  const orderLabel = isItalian ? 'Ordine' : 'Order'

  const handleSelectOrder = () => {
    onSelectOrder(order.id)
  }

  return (
    <motion.article
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={handleSelectOrder}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleSelectOrder()
        }
      }}
      whileHover={reducedMotion ? undefined : { y: -2 }}
      whileTap={reducedMotion ? undefined : { scale: 0.995 }}
      transition={customerMotionTransition}
      className={joinClassNames(
        'relative overflow-hidden rounded-xl border transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-focus-ring)_40%,transparent)]',
        selected
          ? 'border-(--color-border-strong) bg-(--color-bg-surface-muted) shadow-(--shadow-sm) ring-1 ring-(--color-border-subtle) hover:shadow-(--shadow-md)'
          : 'border-(--color-border-default) bg-(--color-bg-card) hover:border-(--color-border-strong) hover:bg-(--color-bg-surface-muted) hover:shadow-(--shadow-md)',
      )}
    >
      <span
        aria-hidden
        className={joinClassNames(
          'absolute inset-y-0 left-0 w-1 transition-opacity',
          selected
            ? 'w-[3px] bg-(--color-action-primary) opacity-100'
            : 'w-[3px] bg-(--color-border-strong) opacity-0',
        )}
      />
      <div className="space-y-3.5 px-4 py-3.5">
        <div className="flex items-start justify-between gap-3">
          <p className="truncate text-sm font-semibold tracking-tight text-(--color-text-primary)">
            {orderLabel} #{order.orderNumber}
          </p>
          <span
            className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${toOrderStatusBadgeClasses(
              order.status,
            )}`}
          >
            {toOrderStatusLabel(order.status, language)}
          </span>
        </div>

        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-(--color-text-primary)">{firstProductName}</p>
            <p className="mt-1 truncate text-xs text-(--color-text-secondary)">{additionalItemsLabel}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <motion.button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  onOpenOrderDetail(order.id)
                }}
                whileTap={reducedMotion ? undefined : { scale: 0.985 }}
                transition={customerMotionTransition}
                className="ca-btn-primary inline-flex h-8 items-center rounded-md px-2.5 text-xs font-semibold"
              >
                {isItalian ? 'Vedi dettagli ordine' : 'View order details'}
              </motion.button>
              <motion.button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  onOpenCatalog()
                }}
                whileTap={reducedMotion ? undefined : { scale: 0.985 }}
                transition={customerMotionTransition}
                className="ca-btn-secondary inline-flex h-8 items-center px-2.5 text-xs font-semibold"
              >
                {isItalian ? 'Compra di nuovo' : 'Buy again'}
              </motion.button>
              <motion.button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  onRequestSupport(order)
                }}
                whileTap={reducedMotion ? undefined : { scale: 0.985 }}
                transition={customerMotionTransition}
                className="ca-btn-tertiary inline-flex h-8 items-center gap-1 px-2.5 text-xs font-medium hover:underline hover:underline-offset-4"
              >
                <HelpCircleIcon className="h-3.5 w-3.5" />
                {isItalian ? 'Richiedi supporto' : 'Request support'}
              </motion.button>
            </div>
          </div>
          <div className="text-left sm:border-l sm:border-(--color-border-default) sm:pl-4 sm:text-right">
            <p className="text-[10px] uppercase tracking-wide text-(--color-text-muted)">{t('customer.orders.total')}</p>
            <p className="mt-1 text-lg font-semibold tracking-tight text-(--color-text-primary)">
              {formatCurrency(order.total, locale)}
            </p>
          </div>
        </div>

        <div className="border-t border-(--color-border-default) pt-3">
          <p className="text-xs text-(--color-text-muted)">{t('customer.orders.createdAt', { date: createdAtLabel })}</p>
        </div>
      </div>
    </motion.article>
  )
}
